import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Loader2, CheckCircle2, Flame } from "lucide-react";

const GOALS = ["Fat loss", "Muscle gain", "Recomp (lose fat + gain muscle)", "Performance / strength", "General health", "Build the habit"];
const EXPERIENCE = ["Beginner", "Intermediate", "Advanced"];
const EQUIPMENT = ["Full gym", "Home weights (dumbbells only)", "Bodyweight only", "Mix"];
const DAYS = [2, 3, 4, 5, 6];

export default function IntakeFormModal({ open, onClose, onDone }) {
    const [form, setForm] = useState({
        primary_goal: "",
        experience: "Beginner",
        equipment: "Full gym",
        injuries: "",
        days_per_week: 3,
        height_ft: "",
        height_in: "",
        current_weight: "",
        goal_weight: "",
        weight_unit: "lb",
        notes: "",
    });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [step, setStep] = useState(1);

    if (!open) return null;

    const submit = async () => {
        setErr(""); setBusy(true);
        try {
            await api.post("/me/intake", {
                ...form,
                days_per_week: Number(form.days_per_week) || 3,
                height_ft: form.height_ft ? Number(form.height_ft) : null,
                height_in: form.height_in ? Number(form.height_in) : null,
                current_weight: form.current_weight ? Number(form.current_weight) : null,
                goal_weight: form.goal_weight ? Number(form.goal_weight) : null,
            });
            if (onDone) onDone();
            onClose();
        } catch (e) {
            setErr(formatApiError(e.response?.data?.detail) || e.message);
        } finally { setBusy(false); }
    };

    const totalSteps = 3;

    return (
        <div className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4" data-testid="intake-modal">
            <div className="bg-[var(--surface)] border border-[var(--gold)] w-full max-w-xl max-h-[95vh] flex flex-col relative overflow-hidden">
                <div className="absolute inset-0 bg-stripes opacity-10 pointer-events-none" />
                <div className="relative flex-1 flex flex-col overflow-hidden">
                    <div className="p-6 border-b border-[var(--border)]">
                        <div className="flex items-center gap-2 mb-2">
                            <Flame size={14} className="text-[var(--gold-bright)]" />
                            <p className="label-tiny text-[var(--gold-bright)]">/ Step {step} of {totalSteps}</p>
                        </div>
                        <h2 className="font-serif text-3xl md:text-4xl leading-tight">
                            {step === 1 && "Before we start, I want to know you."}
                            {step === 2 && "Your body, your baseline."}
                            {step === 3 && "Anything else King should know?"}
                        </h2>
                        <p className="text-sm text-[var(--text-muted)] mt-2">Takes 2 minutes. Skippable fields are fine — fill what you're sure about.</p>
                    </div>
                    <div className="flex-1 overflow-auto p-6 space-y-5">
                        {step === 1 && (
                            <>
                                <div>
                                    <label className="label-tiny block mb-3">What's your main goal?</label>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                        {GOALS.map((g) => (
                                            <button type="button" key={g} onClick={() => setForm({ ...form, primary_goal: g })}
                                                className={`p-3 text-left text-sm border transition-colors ${form.primary_goal === g ? "border-[var(--gold)] bg-[var(--surface-hover)] text-[var(--gold-bright)]" : "border-[var(--border)] hover:border-[var(--gold-deep)]"}`}
                                                data-testid={`intake-goal-${g.slice(0, 6).replace(/\s/g, "")}`}>
                                                {g}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div>
                                    <label className="label-tiny block mb-3">Training experience</label>
                                    <div className="grid grid-cols-3 gap-2">
                                        {EXPERIENCE.map((x) => (
                                            <button type="button" key={x} onClick={() => setForm({ ...form, experience: x })}
                                                className={`py-2 text-xs font-mono tracking-[0.2em] uppercase border transition-colors ${form.experience === x ? "bg-[var(--gold)] text-[#0a0908] border-[var(--gold)]" : "border-[var(--border-bright)] hover:border-[var(--gold-deep)]"}`}
                                                data-testid={`intake-exp-${x.toLowerCase()}`}>
                                                {x}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div>
                                    <label className="label-tiny block mb-3">Equipment access</label>
                                    <div className="grid grid-cols-2 gap-2">
                                        {EQUIPMENT.map((e) => (
                                            <button type="button" key={e} onClick={() => setForm({ ...form, equipment: e })}
                                                className={`py-2 px-3 text-xs border transition-colors text-left ${form.equipment === e ? "border-[var(--gold)] bg-[var(--surface-hover)] text-[var(--gold-bright)]" : "border-[var(--border)] hover:border-[var(--gold-deep)]"}`}>
                                                {e}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div>
                                    <label className="label-tiny block mb-3">Days per week you can train</label>
                                    <div className="grid grid-cols-5 gap-2">
                                        {DAYS.map((d) => (
                                            <button type="button" key={d} onClick={() => setForm({ ...form, days_per_week: d })}
                                                className={`py-3 font-display text-2xl border transition-colors ${form.days_per_week === d ? "bg-[var(--gold)] text-[#0a0908] border-[var(--gold)]" : "border-[var(--border-bright)] hover:border-[var(--gold-deep)]"}`}>
                                                {d}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </>
                        )}

                        {step === 2 && (
                            <>
                                <div>
                                    <label className="label-tiny block mb-3">Height</label>
                                    <div className="flex items-center gap-3">
                                        <div className="flex-1">
                                            <input type="number" min="3" max="8" className="input-dark" value={form.height_ft} onChange={(e) => setForm({ ...form, height_ft: e.target.value })} placeholder="ft" data-testid="intake-height-ft" />
                                        </div>
                                        <div className="flex-1">
                                            <input type="number" min="0" max="11" className="input-dark" value={form.height_in} onChange={(e) => setForm({ ...form, height_in: e.target.value })} placeholder="in" data-testid="intake-height-in" />
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <label className="label-tiny block mb-3">Unit</label>
                                    <div className="grid grid-cols-2 gap-2 w-48">
                                        {["lb", "kg"].map((u) => (
                                            <button type="button" key={u} onClick={() => setForm({ ...form, weight_unit: u })}
                                                className={`py-2 text-xs font-mono tracking-[0.2em] uppercase border ${form.weight_unit === u ? "bg-[var(--gold)] text-[#0a0908] border-[var(--gold)]" : "border-[var(--border-bright)] hover:border-[var(--gold-deep)]"}`}>
                                                {u}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="label-tiny block mb-3">Current weight</label>
                                        <input type="number" step="0.1" className="input-dark" value={form.current_weight} onChange={(e) => setForm({ ...form, current_weight: e.target.value })} placeholder={`in ${form.weight_unit}`} data-testid="intake-current-weight" />
                                    </div>
                                    <div>
                                        <label className="label-tiny block mb-3">Goal weight</label>
                                        <input type="number" step="0.1" className="input-dark" value={form.goal_weight} onChange={(e) => setForm({ ...form, goal_weight: e.target.value })} placeholder={`in ${form.weight_unit}`} data-testid="intake-goal-weight" />
                                    </div>
                                </div>
                            </>
                        )}

                        {step === 3 && (
                            <>
                                <div>
                                    <label className="label-tiny block mb-3">Injuries, limitations, or medical concerns</label>
                                    <textarea className="input-dark min-h-[100px]" value={form.injuries} onChange={(e) => setForm({ ...form, injuries: e.target.value })} placeholder="e.g. Previous knee surgery, lower back sensitive, no shoulder overhead pressing…" data-testid="intake-injuries" />
                                </div>
                                <div>
                                    <label className="label-tiny block mb-3">Anything else King should know?</label>
                                    <textarea className="input-dark min-h-[100px]" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Preferences, past results, what's worked or hasn't, schedule quirks…" />
                                </div>
                            </>
                        )}

                        {err && <div className="text-sm text-[var(--danger)]" data-testid="intake-error">{err}</div>}
                    </div>

                    <div className="border-t border-[var(--border)] p-4 flex items-center justify-between gap-3">
                        {step > 1 ? (
                            <button onClick={() => setStep(step - 1)} className="btn-ghost" data-testid="intake-back">← Back</button>
                        ) : <span />}
                        {step < totalSteps ? (
                            <button onClick={() => setStep(step + 1)} className="btn-primary inline-flex items-center gap-2" data-testid="intake-next">Next →</button>
                        ) : (
                            <button onClick={submit} disabled={busy} className="btn-primary inline-flex items-center gap-2" data-testid="intake-submit">
                                {busy ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : <><CheckCircle2 size={14} /> Finish & start training</>}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
